import React, { useState } from 'react';
import { MapPin, Car, Users, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Clock, Phone, Camera, CreditCard, Banknote, Landmark, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { TrustRow, WhyChoose, OurServices, GoogleReviews, FAQ } from '../components/SharedSections';

const TourStageIndicator = ({ stage }) => (
  <div className="flex items-center justify-center mb-8">
    {[1, 2, 3].map((s) => (
      <div key={s} className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
          s === stage ? 'bg-[#10B981] text-white' : s < stage ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-gray-200 text-gray-400'
        }`}>
          {s < stage ? <CheckCircle2 size={16} /> : s}
        </div>
        {s < 3 && <div className={`w-8 sm:w-12 h-0.5 ${s < stage ? 'bg-[#10B981]' : 'bg-gray-200'}`} />}
      </div>
    ))}
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const toursTrustItems = [
  { textRo: 'Program Flexibil', textEn: 'Flexible Schedule', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { textRo: 'Atracții Importante', textEn: 'Key Attractions', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
  { textRo: 'Autoturisme Moderne', textEn: 'Modern Vehicles', icon: 'M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10' },
  { textRo: 'Atenție la Detalii', textEn: 'Attention to Detail', icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
];

const attractions = [
  {
    id: 'attraction-bran',
    titleRo: 'Castelul Bran',
    titleEn: 'Bran Castle',
    descRo: 'Descoperă unul dintre cele mai cunoscute obiective turistice din România într-un tur privat către Castelul Bran, faimos pentru arhitectura medievală și legenda lui Dracula. După vizitarea castelului, continuăm experiența cu o oprire la Cetatea Râșnov, una dintre cele mai impresionante fortificații medievale din Transilvania. La întoarcere, dacă îți dorești, putem încheia ziua cu un prânz sau o cină la cel mai cautat restaurant din Romania, la Sura Dacilor din Poiana Brașov, înconjurat de peisajele spectaculoase ale munților. Turul include transport premium, program flexibil și preluare de la hotel, aeroport sau orice adresă din Brașov.',
    descEn: 'Discover one of Romania\'s most famous landmarks on a private tour to Bran Castle, renowned for its medieval architecture and the legend of Dracula. After visiting the castle, the experience continues with a stop at Râșnov Fortress, one of Transylvania\'s most impressive medieval fortifications. On the way back, if you wish, we can end the day with lunch or dinner at Romania\'s most sought-after restaurant, Sura Dacilor in Poiana Brașov, surrounded by spectacular mountain scenery. The tour includes premium transport, flexible schedule, and pickup from your hotel, airport, or any address in Brașov.',
    image: '/images/castelul-bran.webp',
  },
  {
    id: 'attraction-peles',
    titleRo: 'Castelul Peleș & Castelul Cantacuzino',
    titleEn: 'Peleș Castle & Cantacuzino Castle',
    descRo: 'Vizitează două dintre cele mai spectaculoase castele din România într-o singură excursie privată. Explorează eleganța regală a Castelului Peleș din Sinaia și continuă către impresionantul Castel Cantacuzino din Bușteni, devenit celebru la nivel internațional ca locație de filmare pentru serialul Wednesday produs de Netflix. Turul include transport privat, program flexibil și opriri pentru fotografii pe Valea Prahovei, oferindu-ți o experiență completă într-una dintre cele mai frumoase regiuni ale României.',
    descEn: 'Visit two of Romania\'s most spectacular castles in a single private excursion. Explore the royal elegance of Peleș Castle in Sinaia and continue to the impressive Cantacuzino Castle in Bușteni, which gained international fame as a filming location for the Netflix series Wednesday. The tour includes private transport, a flexible schedule, and photo stops along the Prahova Valley, offering a complete experience in one of Romania\'s most beautiful regions.',
    image: '/images/castelul-peles.webp',
  },
  {
    id: 'attraction-transfagarasan',
    titleRo: 'Transfăgărășan',
    titleEn: 'Transfăgărășan',
    descRo: 'Descoperă unul dintre cele mai spectaculoase drumuri alpine din lume într-un tur privat pe Transfăgărășan. Admiră peisaje impresionante, Lacul Bâlea, Cascada Capra și serpentinele celebre care traversează Munții Făgăraș. O experiență perfectă pentru iubitorii de natură, fotografie și aventură, cu transport confortabil și itinerariu personalizat.',
    descEn: 'Discover one of the world\'s most spectacular alpine roads on a private tour along the Transfăgărășan. Admire breathtaking landscapes, Bâlea Lake, Capra Waterfall, and the famous switchbacks crossing the Făgăraș Mountains. A perfect experience for nature lovers, photography enthusiasts, and adventure seekers, with comfortable transport and a personalized itinerary.',
    image: '/images/transfagarasan.webp',
  },
  {
    id: 'attraction-sighisoara-viscri',
    titleRo: 'Sighișoara & Viscri',
    titleEn: 'Sighișoara & Viscri',
    descRo: 'Explorează Cetatea Medievală Sighișoara, unul dintre cele mai bine conservate orașe medievale din Europa și inclus în patrimoniul UNESCO. Plimbă-te pe străzile pietruite, vizitează Turnul cu Ceas și descoperă locul de naștere al lui Vlad Țepeș. Turul continuă spre satul Viscri, renumit pentru autenticitatea sa și pentru casa deținută aici de Regele Charles al III-lea, devenită un simbol al conservării patrimoniului rural transilvănean. O experiență autentică ce îmbină istoria, cultura și tradițiile Transilvaniei.',
    descEn: 'Explore the Medieval Citadel of Sighișoara, one of Europe\'s best-preserved medieval towns and a UNESCO World Heritage Site. Stroll along cobblestone streets, visit the Clock Tower, and discover the birthplace of Vlad the Impaler. The tour continues to the village of Viscri, renowned for its authenticity and the house owned by King Charles III, which has become a symbol of Transylvanian rural heritage conservation. An authentic experience blending history, culture, and the traditions of Transylvania.',
    image: '/images/sighisoara-viscri.webp',
  },
  {
    id: 'attraction-transbucegi',
    titleRo: 'Transbucegi',
    titleEn: 'Transbucegi',
    descRo: 'Descoperă spectaculosul drum Transbucegi, una dintre cele mai frumoase rute montane din România. Vizitează formațiunile naturale Babele și Sfinxul, admiră peisajele impresionante ale Munților Bucegi și fă o oprire la pitorescul Lacul Bolboci, supranumit "Marea din Bucegi". Turul privat include transport premium, opriri pentru fotografii și un itinerariu flexibil, perfect pentru iubitorii de natură și aventură.',
    descEn: 'Discover the spectacular Transbucegi road, one of Romania\'s most beautiful mountain routes. Visit the natural rock formations of Babele and the Sphinx, admire the stunning landscapes of the Bucegi Mountains, and stop at the picturesque Bolboci Lake, known as the "Sea of Bucegi." The private tour includes premium transport, photo stops, and a flexible itinerary, perfect for nature and adventure lovers.',
    image: '/images/transbucegi.webp',
  },
  {
    id: 'attraction-sighisoara-turda-sibiu',
    titleRo: 'Sighișoara + Salina Turda + Sibiu',
    titleEn: 'Sighișoara + Turda Salt Mine + Sibiu',
    descRo: 'Pornește într-o experiență completă prin inima Transilvaniei și descoperă trei dintre cele mai apreciate destinații turistice din România. Vizitează cetatea medievală Sighișoara, explorează spectaculoasa Salina Turda, considerată una dintre cele mai frumoase saline din lume, iar seara bucură-te de o plimbare relaxantă prin centrul istoric al Sibiului, urmată de cină într-unul dintre restaurantele din Piața Mare. Turul privat include transport premium, itinerariu flexibil și suficient timp pentru a descoperi cele mai reprezentative atracții ale Transilvaniei.',
    descEn: 'Embark on a complete experience through the heart of Transylvania and discover three of Romania\'s most acclaimed tourist destinations. Visit the medieval citadel of Sighișoara, explore the spectacular Turda Salt Mine — considered one of the most beautiful salt mines in the world — and in the evening, enjoy a relaxing walk through the historic center of Sibiu, followed by dinner at one of the restaurants in the Grand Square. The private tour includes premium transport, a flexible itinerary, and plenty of time to discover Transylvania\'s most iconic attractions.',
    image: '/images/salina-turda-sibiu.webp',
  },
];

const tourCarTypes = [
  { id: 'standard', label: 'STANDARD', desc: 'Skoda Superb', descFull: 'max 3 persoane', descFullEn: 'max 3 persons', maxPax: 3 },
  { id: 'premium', label: 'PREMIUM', desc: 'Mercedes E300de', descFull: 'max 3 persoane', descFullEn: 'max 3 persons', maxPax: 3 },
  { id: 'minivan', label: 'MINIVAN 7+1', desc: 'Mercedes Vito Tourer', descFull: 'max 7 persoane', descFullEn: 'max 7 persons', maxPax: 7 },
];

const tourRoutes = [
  { id: 'tour-bran', nameRo: 'Castelul Bran + Cetatea Râșnov', nameEn: 'Bran Castle + Râșnov Fortress', prices: { standard: 500, premium: 600, minivan: 700 } },
  { id: 'tour-peles', nameRo: 'Castelul Peleș + Castelul Cantacuzino', nameEn: 'Peleș Castle + Cantacuzino Castle', prices: { standard: 500, premium: 600, minivan: 700 } },
  { id: 'tour-transfagarasan', nameRo: 'Transfăgărășan', nameEn: 'Transfăgărășan', prices: { standard: 650, premium: 750, minivan: 850 } },
  { id: 'tour-sighisoara-viscri', nameRo: 'Sighișoara & Viscri', nameEn: 'Sighișoara & Viscri', prices: { standard: 700, premium: 825, minivan: 950 } },
  { id: 'tour-transbucegi', nameRo: 'Transbucegi', nameEn: 'Transbucegi', prices: { standard: 650, premium: 750, minivan: 850 } },
  { id: 'tour-sighisoara-turda-sibiu', nameRo: 'Sighișoara + Salina Turda + Sibiu', nameEn: 'Sighișoara + Turda Salt Mine + Sibiu', prices: { standard: 1400, premium: 1600, minivan: 1800 } },
];

const ToursPage = ({ language }) => {
  const isRo = language === 'ro';
  const [selectedCarType, setSelectedCarType] = useState('standard');
  const [selectedTour, setSelectedTour] = useState(null);
  const [stage, setStage] = useState(1);
  const [formData, setFormData] = useState({
    date: null,
    time: '09:00',
    passengers: '1',
    address: '',
    fullName: '',
    email: '',
    phone: '',
    paymentMethod: 'card',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalAttraction, setModalAttraction] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToForm = () => {
    setTimeout(() => {
      document.getElementById('tour-booking-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const goToStage2 = () => {
    setError('');
    if (!selectedTour || !formData.date) {
      setError(isRo ? 'Selectează turul și data dorită.' : 'Please select a tour and date.');
      return;
    }
    if (parseInt(formData.passengers) > 3) {
      setSelectedCarType('minivan');
    }
    setStage(2);
    scrollToForm();
  };

  const goToStage3 = () => {
    setError('');
    setStage(3);
    scrollToForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.fullName || !formData.phone) {
      setError(isRo ? 'Completează numele și telefonul.' : 'Please fill in name and phone.');
      return;
    }
    setLoading(true);
    try {
      const tour = tourRoutes.find(t => t.id === selectedTour);
      const car = tourCarTypes.find(c => c.id === selectedCarType);
      const price = tour.prices[selectedCarType];
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          message: [
            'TOUR BOOKING REQUEST',
            `Tour: ${isRo ? tour.nameRo : tour.nameEn}`,
            `Vehicle: ${car.label} (${car.desc})`,
            `Date: ${format(formData.date, 'PPP')}, ${formData.time}`,
            `Passengers: ${formData.passengers}`,
            `Pickup: ${formData.address || '-'}`,
            `Payment: ${formData.paymentMethod === 'card' ? 'Online card' : formData.paymentMethod === 'transfer' ? 'Bank transfer' : 'Cash'}`,
            price > 0 ? `Price: ${price} RON` : 'Price: to be confirmed',
            `Notes: ${formData.notes || '-'}`,
          ].join('\n'),
        })
      });
      setSubmitted(true);
      // GTM: Track tour booking conversion
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'booking_submitted',
        booking_type: 'tour',
        tour_name: isRo ? tour.nameRo : tour.nameEn,
        vehicle_type: car.label,
        booking_value: price,
        currency: 'RON',
      });
    } catch (err) {
      setError(isRo ? 'Eroare la trimitere. Încercați din nou.' : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setStage(1);
    setSelectedTour(null);
    setSelectedCarType('standard');
    setFormData({ date: null, time: '09:00', passengers: '1', address: '', fullName: '', email: '', phone: '', paymentMethod: 'card', notes: '' });
  };

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-28 sm:pt-0">
        <div className="absolute inset-0">
          <img src="/images/tours-cover.webp" alt="Touristic Tours" className="w-full h-full object-cover" />
          <div className="absolute inset-0 md:hidden" style={{ backgroundColor: 'rgba(255,255,255,0.80)' }}></div>
          <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.90) 35%, rgba(255,255,255,0.60) 60%, rgba(255,255,255,0) 100%)' }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10 py-20">
          <div className="max-w-2xl">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Tururi Turistice' : 'Touristic Tours'}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0D1B3E] mb-4 leading-tight">
              {isRo ? 'Descoperă Brașovul și împrejurimile' : 'Discover Brașov & surroundings'}
            </h1>
            <p className="font-body text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
              {isRo
                ? 'Tururi private cu transport inclus la cele mai frumoase obiective turistice din zonă. Castelul Bran, Castelul Peleș și multe altele.'
                : 'Private tours with included transport to the most beautiful landmarks in the area. Bran Castle, Peleș Castle and more.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => scrollTo('tour-booking-form')} data-testid="tours-cta-primary"
                className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all cursor-pointer">
                {isRo ? 'Rezervă Acum' : 'Book Now'}
              </button>
              <button onClick={() => scrollTo('attractions')} data-testid="tours-cta-secondary"
                className="inline-flex items-center justify-center bg-[#0D1B3E] hover:bg-[#1A3A6B] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all cursor-pointer">
                {isRo ? 'Vezi Atracțiile' : 'See Attractions'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Row — custom items for Tours */}
      <TrustRow isRo={isRo} items={toursTrustItems} />

      {/* Main Attractions */}
      <section id="attractions" className="py-20 sm:py-28 bg-white" data-testid="tours-attractions">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Atracții Principale' : 'Main Attractions'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E] mb-4">
              {isRo ? 'Descoperă Cele Mai Frumoase Atracții din Transilvania' : 'Discover the Most Beautiful Attractions in Transylvania'}
            </h2>
            <p className="font-body text-base text-gray-500 max-w-3xl mx-auto leading-relaxed">
              {isRo
                ? 'Alege unul dintre tururile private Montréon și explorează cele mai spectaculoase destinații din România. Transport premium, program flexibil și experiențe memorabile, concepute pentru a descoperi Transilvania în cel mai confortabil mod posibil.'
                : 'Choose one of the Montréon private tours and explore Romania\'s most spectacular destinations. Premium transport, flexible schedule, and memorable experiences designed to discover Transylvania in the most comfortable way possible.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {attractions.map((a) => (
              <div key={a.id} className="rounded-xl overflow-hidden group transition-all hover:shadow-xl"
                data-testid={a.id}
                style={{ backgroundColor: '#fff', border: '1px solid rgba(13,27,62,0.08)', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <div className="relative h-52 overflow-hidden">
                  <img src={a.image} alt={isRo ? a.titleRo : a.titleEn} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,27,62,0.55) 0%, transparent 50%)' }}></div>
                  <h3 className="absolute bottom-3 left-4 right-4 font-display text-base font-bold text-white drop-shadow-lg">
                    {isRo ? a.titleRo : a.titleEn}
                  </h3>
                </div>
                <div className="p-5">
                  <p className="font-body text-sm text-gray-500 leading-relaxed line-clamp-4">
                    {isRo ? a.descRo : a.descEn}
                  </p>
                  <button onClick={() => setModalAttraction(a)} data-testid={`read-more-${a.id}`}
                    className="mt-3 text-[#10B981] text-sm font-semibold hover:underline cursor-pointer">
                    {isRo ? 'Citește mai mult →' : 'Read more →'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center p-6 rounded-xl max-w-3xl mx-auto" style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
            <p className="font-body text-sm text-gray-600">
              <span className="font-semibold text-[#0D1B3E]">{isRo ? 'Tur personalizat?' : 'Custom tour?'}</span>{' '}
              {isRo
                ? 'Contactează-ne și îți oferim cu plăcere un preț special și un itinerariu personalizat, adaptat preferințelor tale.'
                : 'Contact us and we\'ll happily offer you a special price and a personalized itinerary tailored to your preferences.'}
              {' '}
              <a href="/contact" className="text-[#10B981] font-semibold hover:underline">{isRo ? 'Contactează-ne' : 'Contact us'}</a>
            </p>
          </div>
        </div>
      </section>

      {/* Price List */}
      <section id="tour-prices" className="py-20 sm:py-28" style={{ backgroundColor: '#F8FAFC' }} data-testid="tours-prices">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Prețuri Tururi' : 'Tour Prices'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E] mb-4">
              {isRo ? 'Alege turul tău' : 'Choose your tour'}
            </h2>
            <p className="font-body text-base text-gray-500 max-w-xl mx-auto">
              {isRo
                ? 'Prețurile includ transport privat din Brașov și înapoi.'
                : 'Prices include private transport from Brașov and back.'}
            </p>
          </div>

          {/* Car Type Selector */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12">
            {tourCarTypes.map((car) => (
              <button key={car.id} onClick={() => setSelectedCarType(car.id)} data-testid={`tour-car-type-${car.id}`}
                className="w-full sm:w-auto px-6 py-4 rounded-xl text-center transition-all"
                style={selectedCarType === car.id
                  ? { backgroundColor: '#0D1B3E', color: '#fff', boxShadow: '0 8px 30px rgba(13,27,62,0.25)' }
                  : { backgroundColor: 'rgba(255,255,255,1)', color: '#0D1B3E', border: '1px solid rgba(13,27,62,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
                }>
                <span className="font-display text-sm font-bold tracking-wide block">{car.label}</span>
                <span className="text-xs block mt-1" style={{ opacity: 0.7 }}>{car.desc} · {isRo ? car.descFull : car.descFullEn}</span>
              </button>
            ))}
          </div>

          {/* Tour Price Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {tourRoutes.map((route) => {
              const price = route.prices[selectedCarType];
              return (
                <div key={route.id} className="rounded-xl p-6 transition-all hover:shadow-lg"
                  data-testid={`tour-price-${route.id}`}
                  style={{ backgroundColor: '#fff', border: '2px solid transparent', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.10)' }}>
                      <Camera className="text-[#10B981]" size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-sm font-bold text-[#0D1B3E] leading-snug mb-3">{isRo ? route.nameRo : route.nameEn}</h3>
                      {price > 0 ? (
                        <div className="flex items-baseline gap-1">
                          <span className="font-display text-2xl font-bold text-[#0D1B3E]">{price}</span>
                          <span className="text-gray-400 text-sm font-medium">RON</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm italic">{isRo ? 'Preț în curând' : 'Price coming soon'}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm italic text-gray-400 mt-10 max-w-3xl mx-auto">
            {isRo
              ? '* Prețurile includ transport privat din Brașov și înapoi. Pot varia în funcție de itinerariu și durata turului.'
              : '* Prices include private transport from Brașov and back. May vary depending on the itinerary and tour duration.'}
          </p>

          <div className="mt-8 text-center p-6 rounded-xl max-w-3xl mx-auto" style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
            <p className="font-body text-sm text-gray-600">
              <span className="font-semibold text-[#0D1B3E]">{isRo ? 'Tur personalizat?' : 'Custom tour?'}</span>{' '}
              {isRo
                ? 'Contactează-ne și îți oferim cu plăcere un preț special și un itinerariu personalizat, adaptat preferințelor tale.'
                : 'Contact us and we\'ll happily offer you a special price and a personalized itinerary tailored to your preferences.'}
              {' '}
              <a href="/contact" className="text-[#10B981] font-semibold hover:underline">{isRo ? 'Contactează-ne' : 'Contact us'}</a>
            </p>
          </div>
        </div>
      </section>

      {/* ── 3-Stage Tour Booking Form ── */}
      <section id="tour-booking-form" className="py-20 sm:py-28 bg-[#0D1B3E]" data-testid="tours-booking-form">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Rezervare Tur' : 'Book a Tour'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              {isRo ? 'Rezervă Turul Tău' : 'Book Your Tour'}
            </h2>
          </div>

          <div className={`mx-auto ${stage === 2 ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="bg-white rounded-xl shadow-2xl">
              <div className="p-6 sm:p-10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="tour-booking-success">
                    <CheckCircle2 className="mx-auto text-[#10B981] mb-6" size={56} strokeWidth={1.5} />
                    <p className="font-body text-lg text-gray-700 max-w-md mx-auto">
                      {isRo
                        ? 'Vă mulțumim că ați ales serviciile noastre. Echipa noastră vă va contacta în cel mai scurt timp posibil pentru confirmarea rezervării.'
                        : 'Thank you for choosing our services. Our team will contact you as soon as possible to confirm your booking.'}
                    </p>
                    <button onClick={resetForm} className="mt-6 text-[#10B981] text-sm font-medium hover:underline cursor-pointer" data-testid="tour-new-booking-btn">
                      {isRo ? 'Rezervare nouă' : 'New booking'}
                    </button>
                  </div>
                ) : (
                  <>
                    <TourStageIndicator stage={stage} />

                    {/* ===== STAGE 1: Tour, Date, Passengers ===== */}
                    {stage === 1 && (
                      <div className="space-y-5" data-testid="tour-booking-stage-1">
                        {/* Tour Selection */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Selectează Turul' : 'Select Tour'} *
                          </label>
                          <Select onValueChange={setSelectedTour} value={selectedTour || ''}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="select-tour">
                              <SelectValue placeholder={isRo ? 'Alege turul dorit' : 'Choose your tour'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                              {tourRoutes.map((tour) => (
                                <SelectItem key={tour.id} value={tour.id} className="text-gray-900 hover:bg-gray-100 rounded-lg">
                                  {isRo ? tour.nameRo : tour.nameEn}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Date & Time */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Data și Ora' : 'Date & Time'} *
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" data-testid="tour-select-date"
                                  className="w-full justify-start text-left bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100 rounded-lg h-12">
                                  <CalendarIcon className="mr-3 h-4 w-4 text-[#10B981]" />
                                  {formData.date ? format(formData.date, 'PPP') : <span className="text-gray-400">{isRo ? 'Selectează data' : 'Select date'}</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-white border-gray-200 rounded-lg shadow-lg">
                                <Calendar mode="single" selected={formData.date}
                                  onSelect={(date) => setFormData({...formData, date})}
                                  disabled={(date) => date < new Date()} className="bg-white text-gray-900" />
                              </PopoverContent>
                            </Popover>
                            <Input type="time" value={formData.time}
                              onChange={(e) => setFormData({...formData, time: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="tour-input-time" />
                          </div>
                        </div>

                        {/* Passengers */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Număr Persoane' : 'Number of Persons'}
                          </label>
                          <Select onValueChange={(val) => setFormData({...formData, passengers: val})} value={formData.passengers}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="tour-select-passengers">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                              {[1,2,3,4,5,6,7].map(n => (
                                <SelectItem key={n} value={n.toString()} className="text-gray-900 hover:bg-gray-100 rounded-lg">
                                  {n} {n === 1 ? (isRo ? 'persoană' : 'person') : (isRo ? 'persoane' : 'persons')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Pickup Address */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Adresa de Preluare' : 'Pickup Address'}
                          </label>
                          <Input value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                            className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                            placeholder={isRo ? 'Hotel, adresă, aeroport...' : 'Hotel, address, airport...'}
                            data-testid="tour-input-address" />
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="tour-form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        <Button onClick={goToStage2} data-testid="tour-stage1-cta"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {isRo ? 'Alege Vehiculul' : 'Choose Vehicle'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </div>
                    )}

                    {/* ===== STAGE 2: Vehicle Selection ===== */}
                    {stage === 2 && (
                      <div className="space-y-6" data-testid="tour-booking-stage-2">
                        <button type="button" onClick={() => { setStage(1); setError(''); scrollToForm(); }}
                          className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2 cursor-pointer">
                          <ArrowLeft size={16} className="mr-1" />
                          {isRo ? 'Înapoi la detalii' : 'Back to details'}
                        </button>

                        {/* Tour Summary */}
                        {selectedTour && (() => {
                          const tour = tourRoutes.find(t => t.id === selectedTour);
                          return (
                            <div className="p-4 rounded-lg flex flex-wrap items-center gap-3 text-sm" style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                              <span className="font-bold text-[#0D1B3E]">{isRo ? tour.nameRo : tour.nameEn}</span>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-600">{formData.date && format(formData.date, 'dd MMM yyyy')}, {formData.time}</span>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-600">{formData.passengers} {parseInt(formData.passengers) === 1 ? (isRo ? 'persoană' : 'person') : (isRo ? 'persoane' : 'persons')}</span>
                            </div>
                          );
                        })()}

                        <p className="text-sm text-gray-500">
                          {isRo ? 'Selectează vehiculul preferat:' : 'Select your preferred vehicle:'}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {tourCarTypes.filter(car => car.maxPax >= parseInt(formData.passengers)).map((car) => {
                            const tour = tourRoutes.find(t => t.id === selectedTour);
                            const price = tour ? tour.prices[car.id] : 0;
                            const isSelected = selectedCarType === car.id;
                            return (
                              <div key={car.id} onClick={() => setSelectedCarType(car.id)}
                                data-testid={`tour-form-car-${car.id}`}
                                className="cursor-pointer rounded-xl border-2 p-5 transition-all text-center"
                                style={isSelected
                                  ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', boxShadow: '0 8px 30px rgba(16,185,129,0.15)' }
                                  : { borderColor: '#e5e7eb', backgroundColor: '#fff' }
                                }>
                                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                                  style={{ backgroundColor: isSelected ? 'rgba(16,185,129,0.15)' : 'rgba(13,27,62,0.06)' }}>
                                  {car.id === 'minivan'
                                    ? <Users size={22} className={isSelected ? 'text-[#10B981]' : 'text-gray-400'} />
                                    : <Car size={22} className={isSelected ? 'text-[#10B981]' : 'text-gray-400'} />}
                                </div>
                                <h3 className="font-display text-sm font-bold text-[#0D1B3E] mb-0.5">{car.label}</h3>
                                <p className="text-xs text-gray-500 mb-1">{car.desc}</p>
                                <p className="text-xs text-gray-400 mb-3">{isRo ? car.descFull : car.descFullEn}</p>
                                <div className="pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                  {price > 0 ? (
                                    <>
                                      <span className="font-display text-2xl font-bold text-[#0D1B3E]">{price}</span>
                                      <span className="text-gray-400 text-sm ml-1">RON</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-400 text-sm italic">{isRo ? 'La cerere' : 'On request'}</span>
                                  )}
                                </div>
                                {isSelected && <CheckCircle2 size={18} className="text-[#10B981] mx-auto mt-3" />}
                              </div>
                            );
                          })}
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="tour-form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        <Button onClick={goToStage3} data-testid="tour-stage2-cta"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {isRo ? 'Continuă' : 'Continue'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </div>
                    )}

                    {/* ===== STAGE 3: Personal Details ===== */}
                    {stage === 3 && (
                      <form onSubmit={handleSubmit} className="space-y-5" data-testid="tour-booking-stage-3">
                        <button type="button" onClick={() => { setStage(2); setError(''); scrollToForm(); }}
                          className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2 cursor-pointer">
                          <ArrowLeft size={16} className="mr-1" />
                          {isRo ? 'Înapoi la vehicul' : 'Back to vehicle'}
                        </button>

                        {/* Name */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Nume Complet' : 'Full Name'} *
                          </label>
                          <Input value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                            className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                            placeholder={isRo ? 'Nume și prenume' : 'Full name'} data-testid="tour-input-fullname" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Telefon' : 'Phone'} *
                            </label>
                            <Input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                              placeholder="+40 7XX XXX XXX" data-testid="tour-input-phone" />
                          </div>
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">Email</label>
                            <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                              placeholder="email@example.com" data-testid="tour-input-email" />
                          </div>
                        </div>

                        {/* Payment Method */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Metoda de Plată' : 'Payment Method'}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { id: 'card', labelRo: 'Plata online cu card', labelEn: 'Online card payment', icon: CreditCard },
                              { id: 'transfer', labelRo: 'Transfer bancar', labelEn: 'Bank transfer', icon: Landmark },
                              { id: 'cash', labelRo: 'Cash', labelEn: 'Cash', icon: Banknote },
                            ].map((pm) => {
                              const PmIcon = pm.icon;
                              return (
                                <button type="button" key={pm.id} onClick={() => setFormData({...formData, paymentMethod: pm.id})}
                                  data-testid={`tour-payment-${pm.id}`}
                                  className="flex items-center gap-3 p-3 rounded-lg border-2 text-sm font-medium transition-all text-left cursor-pointer"
                                  style={formData.paymentMethod === pm.id
                                    ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                    : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                                  }>
                                  <PmIcon size={18} className="flex-shrink-0" />
                                  <span>{isRo ? pm.labelRo : pm.labelEn}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Observații (opțional)' : 'Notes (optional)'}
                          </label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                            className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg p-3 h-24 resize-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981] text-sm"
                            placeholder={isRo ? 'Detalii suplimentare, cereri speciale...' : 'Additional details, special requests...'}
                            data-testid="tour-input-notes" />
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="tour-form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        {/* Summary — moved above submit */}
                        {selectedTour && (() => {
                          const tour = tourRoutes.find(t => t.id === selectedTour);
                          const car = tourCarTypes.find(c => c.id === selectedCarType);
                          const price = tour.prices[selectedCarType];
                          return (
                            <div className="p-4 bg-[#0D1B3E] rounded-lg" data-testid="tour-price-summary">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                  <p className="text-sm font-medium text-white">{isRo ? tour.nameRo : tour.nameEn}</p>
                                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {car.label} ({car.desc}) · {formData.passengers} {parseInt(formData.passengers) === 1 ? (isRo ? 'persoană' : 'person') : (isRo ? 'persoane' : 'persons')}
                                  </p>
                                </div>
                                {price > 0 ? (
                                  <span className="text-[#10B981] font-bold text-2xl">{price} RON</span>
                                ) : (
                                  <span className="text-[#10B981] font-semibold text-sm">{isRo ? 'Preț la cerere' : 'Price on request'}</span>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <Button type="submit" disabled={loading} data-testid="tour-submit"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {loading
                            ? (isRo ? 'Se procesează...' : 'Processing...')
                            : (isRo ? 'Rezervă Acum' : 'Book Now')}
                        </Button>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhyChoose isRo={isRo} />
      <OurServices isRo={isRo} />
      <GoogleReviews isRo={isRo} />
      <FAQ isRo={isRo} />

      {/* Attraction Detail Modal */}
      {modalAttraction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="attraction-modal-overlay"
          onClick={() => setModalAttraction(null)}>
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(13,27,62,0.70)', backdropFilter: 'blur(4px)' }}></div>
          <div className="relative bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl" data-testid="attraction-modal"
            onClick={(e) => e.stopPropagation()}>
            <div className="relative h-56 sm:h-64 overflow-hidden">
              <img src={modalAttraction.image} alt={isRo ? modalAttraction.titleRo : modalAttraction.titleEn}
                className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,27,62,0.65) 0%, transparent 50%)' }}></div>
              <h2 className="absolute bottom-4 left-5 right-14 font-display text-xl sm:text-2xl font-bold text-white drop-shadow-lg">
                {isRo ? modalAttraction.titleRo : modalAttraction.titleEn}
              </h2>
              <button onClick={() => setModalAttraction(null)} data-testid="close-attraction-modal"
                className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
                <X size={18} />
              </button>
            </div>
            <div className="p-6 sm:p-8 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 16rem)' }}>
              <p className="font-body text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {isRo ? modalAttraction.descRo : modalAttraction.descEn}
              </p>
              <button onClick={() => { setModalAttraction(null); scrollTo('tour-booking-form'); }} data-testid="modal-book-btn"
                className="mt-6 inline-flex items-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-6 py-3 rounded-lg text-sm transition-all cursor-pointer">
                {isRo ? 'Rezervă Acest Tur' : 'Book This Tour'}
                <ArrowRight className="ml-2" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ToursPage;
